import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { User } from '../../../types/auth';
import type { Section } from '../../../types/tender';
import { BaseModal } from '../../common/UI/BaseModal';
import { TenderPermissionsForm } from '../TenderPermissionsForm';

const EMPTY_ARRAY: never[] = [];

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  section: Section | null;
  teamUsers: User[];
  onAssign: (user: User, permissions?: string[]) => void;
  loading?: boolean;
  // Pass the current tender permissions so we can populate the form
  currentMemberPermissions?: Record<string, string[]> | undefined;
  tenderOwnerId?: string | undefined;
}

const EMPTY_PERMISSIONS: Record<string, string[]> = {};

const AssignmentDialog: React.FC<AssignmentDialogProps> = ({
  open,
  onClose,
  section,
  teamUsers,
  onAssign,
  loading = false,
  currentMemberPermissions = EMPTY_PERMISSIONS,
  tenderOwnerId,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [permissions, setPermissions] = useState<string[] | undefined>(undefined);
  const filteredUsers = teamUsers;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedUserId('');
      setPermissions(undefined);
    }
  }, [open]);

  // When a user is selected, populate their existing permissions for this tender
  useEffect(() => {
    if (selectedUserId) {
      setPermissions(currentMemberPermissions[selectedUserId]);
    } else {
      setPermissions(undefined);
    }
  }, [selectedUserId, currentMemberPermissions]);

  const handleAssignClick = () => {
    const user = filteredUsers.find((u) => u.uid === selectedUserId);
    if (user) {
      onAssign(user, permissions);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={`Assign Section: ${section?.title}`}
      contentSx={{ p: 0 }}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleAssignClick}
            variant="contained"
            color="primary"
            disabled={!selectedUserId || loading}
          >
            Assign
          </Button>
        </>
      }
    >
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2">
              Select a team member to assign this section to. You can also adjust their specific
              permissions for this tender.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="assign-user-select-label">Select User</InputLabel>
              <Select
                labelId="assign-user-select-label"
                value={selectedUserId}
                label="Select User"
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                {filteredUsers.map((user) => (
                  <MenuItem key={user.uid} value={user.uid}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {user.displayName?.charAt(0) || user.email?.charAt(0)}
                      </Avatar>
                      {user.displayName || user.email}
                    </Box>
                  </MenuItem>
                ))}
                {filteredUsers.length === 0 && (
                  <MenuItem disabled value="">
                    No eligible team members found.
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {selectedUserId && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}
                >
                  PERMISSIONS FOR THIS TENDER
                </Typography>
                {selectedUserId === tenderOwnerId ? (
                  <Typography variant="body2" color="text.secondary">
                    This user is the Tender Owner and already has Full Access.
                  </Typography>
                ) : (
                  <>
                    {!permissions && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontStyle: 'italic' }}
                      >
                        Inheriting default permissions from their global role.
                      </Typography>
                    )}
                    <TenderPermissionsForm
                      permissions={permissions || EMPTY_ARRAY}
                      onChange={setPermissions}
                      hideNoneOption={true}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default AssignmentDialog;
